import { AllPermissions, EmployeeRole } from 'src/lib/fliko/models/company/employeeRole.data';
import { DEFAULT_OFFER_FEATURE } from 'src/lib/fliko/models/defaults/offer.defaults';
import { LocLabel, LocLabelModifier } from 'src/app/shared/components/clickable-row-table/models/data-table-config.models';
import { DialogService } from 'src/app/shared/services/dialog.service';

export class EmployeeHelper {
  public static async confirmRoleAssignment(role: EmployeeRole, dialogService: DialogService): Promise<boolean> {
    if (role !== EmployeeRole.OFFER_MANAGER) return true;
    return !!(await dialogService
      .openConfirmationDialog({
        textKey: 'guard.assignOfferManager',
        acceptKey: 'button.confirm',
        cancelKey: 'button.cancel'
      })
      .toPromise());
  }

  public static employeePermModuleLL(moduleName: string): LocLabel {
    return EmployeeHelper.employeePermKeyLL(EmployeeHelper.employeePermModuleKey(moduleName));
  }

  public static employeePermKeyLL(key: string): LocLabel {
    return {
      key,
      modifier: LocLabelModifier.CAP,
      replaceMap: { $offerIdentification: DEFAULT_OFFER_FEATURE.identificationStyle }
    };
  }

  /**
   * Get permission module translation key
   * @param {string} moduleName keyof AllPermissions
   * @return {string}
   */
  public static employeePermModuleKey(moduleName: string): string {
    let key = 'navigation.';
    // prettier-ignore
    switch (moduleName) {
      case 'e_data':        key += 'estate';          break;
      case 'e_sub':         key += 'subcontractors';  break;
      case 'e_review':      key += 'reviews';         break;
      case 'e_contact':     key += 'contacts';        break;
      case 'e_docs':        key += 'documents';       break;
      case 'e_util':        key += 'utilities';       break;
      case 'e_renovations': key += 'renovations';     break;
      case 'log':           key += 'eventLog';        break;
      case 'user':          key += 'residents';       break;
      default: key = `${moduleName.includes('c_') ? 'perm' : 'navigation'}.${moduleName}`;
    }
    return key;
  }

  /**
   * Filter all permissions that aren't 'view', 'edit' or 'report'
   * @param {string} key permission key (eg. 'view', 'settings', 'meters')
   * @return {boolean}
   */
  public static filterOtherPerms = (key: string) => !['view', 'edit', 'report'].includes(key);

  /** All true implementation of AllPermissions interface */
  public static readonly allPermissions: AllPermissions = {
    chat: { view: true },
    help: { view: true },
    c_dashboard: { view: true, edit: true },
    calendar: { view: true, edit: true, anyEdit: true },
    news: { view: true, edit: true },
    defects: { view: true, edit: true, report: true, settings: true },
    concierge: { view: true, edit: true, report: true, settings: true },
    resolutions: { view: true, edit: true, report: true, publish: true, remind: true, votes: true },
    polls: { view: true, edit: true, report: true, publish: true, remind: true, votes: true },
    booking: { view: true, edit: true, report: true, block: true },
    reckonings: { view: true, report: true, import: true, meters: true },
    user: { view: true, edit: true, report: true, auth: true },
    e_data: { view: true, edit: true },
    e_sub: { view: true, edit: true, invite: true },
    e_contact: { view: true, edit: true },
    e_review: { view: true, edit: true, report: true },
    e_docs: { view: true, edit: true },
    e_util: { view: true, edit: true },
    e_renovations: { view: true, edit: true },
    reports: { view: true, report: true },
    websites: { view: true, edit: true },
    log: { view: true, report: true },
    e_settings: { view: true, edit: true },
    c_role: { view: true, edit: true, report: true },
    c_report: { view: true, report: true },
    c_log: { view: true, report: true },
    c_calendar: { view: true, edit: true },
    mailing: { review: true, agreement: true, subcontractor: true, renovation: true, utility: true, insurance: true },
    c_mailing: { employee: true, ssoAccess: true },
    c_sso: { view: true, edit: true },
    c_settings: { view: true, edit: true },
    bluebolt: { view: true, edit: true },
    offers: { view: true, edit: true }
  };
}
